// Homepage tools: budget planner, freedom (FIRE) number, currency converter
// and the investor quiz. Markup lives in template-parts/toolkit.php and
// quiz.php; tab switching is the shared data-tabs handler in script.js.
document.addEventListener("DOMContentLoaded", () => {
  const root = document.querySelector(".bz-tools");
  const cur = root ? root.dataset.currency || "$" : "$";
  const num = (id) => {
    const el = document.getElementById(id);
    const v = el ? parseFloat(el.value) : NaN;
    return Number.isFinite(v) ? v : 0;
  };
  const money = (n, c) => (c === undefined ? cur : c) + Math.round(n).toLocaleString("en-US");

  // ─── Budget planner (50 / 30 / 20) ───────────────────────────────────────
  const budget = document.querySelector('[data-tool="budget"]');
  if (budget) {
    const out = budget.querySelector('[data-out="result"]');
    const needs = document.getElementById("bNeeds");
    const wants = document.getElementById("bWants");
    const presets = budget.querySelectorAll("[data-split]");
    const renderBudget = () => {
      let n = Number(needs.value);
      let w = Number(wants.value);
      if (n + w > 100) w = 100 - n;
      wants.value = w;
      const s = 100 - n - w;
      budget.querySelector('[data-val="bNeeds"]').textContent = n + "%";
      budget.querySelector('[data-val="bWants"]').textContent = w + "%";
      const income = num("bIncome");
      const rows = [
        ["Needs", n, "rent, bills, groceries", 1],
        ["Wants", w, "fun, eating out, extras", 2],
        ["Savings", s, "goals and investing", 3],
      ];
      out.innerHTML =
        '<div class="bz-donut" style="--a:' + n + ";--b:" + (n + w) + '"><span><small>per month</small>' + money(income) + "</span></div>" +
        '<ul class="bz-split">' +
        rows.map((r) => '<li class="bz-split__row bz-split__row--' + r[3] + '"><i></i><span>' + r[0] + "<small>" + r[2] + "</small></span><b>" + money((income * r[1]) / 100) + "<small>" + r[1] + "%</small></b></li>").join("") +
        "</ul>" +
        '<p class="bz-tool__pill">That is <b>' + money((income * s * 12) / 100) + "</b> saved over a year.</p>";
    };
    [needs, wants, document.getElementById("bIncome")].forEach((el) => el.addEventListener("input", () => {
      presets.forEach((b) => b.setAttribute("aria-pressed", "false"));
      renderBudget();
    }));
    presets.forEach((b) => b.addEventListener("click", () => {
      const [n, w] = b.dataset.split.split(",");
      needs.value = n;
      wants.value = w;
      presets.forEach((x) => x.setAttribute("aria-pressed", x === b ? "true" : "false"));
      renderBudget();
    }));
    renderBudget();
  }

  // ─── Freedom number ───────────────────────────────────────────────────────
  const fire = document.querySelector('[data-tool="fire"]');
  if (fire) {
    const out = fire.querySelector('[data-out="result"]');
    const yearsTo = (target, saved, add, rate) => {
      let bal = saved;
      let y = 0;
      while (bal < target && y < 100) {
        bal = bal * (1 + rate / 100) + add;
        y++;
      }
      return bal >= target ? y : null;
    };
    const renderFire = () => {
      const spend = num("fSpend");
      const saved = num("fSaved");
      const add = num("fAdd");
      const rate = num("fRate");
      const wr = Math.max(num("fWithdraw"), 0.1);
      const target = spend / (wr / 100);
      const pct = target > 0 ? Math.min(100, (saved / target) * 100) : 0;
      const y = yearsTo(target, saved, add, rate);
      const faster = yearsTo(target, saved, add * 1.2, rate);
      const when = y === null ? "100+ years" : y === 0 ? "You are there" : y + (y === 1 ? " year" : " years");
      let tip = "";
      if (y !== null && y > 0 && faster !== null && faster < y) {
        tip = "Saving 20% more each year (" + money(add * 1.2) + ") would get you there about <b>" + (y - faster) + (y - faster === 1 ? " year" : " years") + "</b> sooner.";
      } else if (y === null) {
        tip = "At this pace the target is out of reach. Try saving more each year or lowering your spending.";
      }
      out.innerHTML =
        '<span class="bz-tool__label">Your freedom number</span>' +
        '<strong class="bz-tool__big">' + money(target) + "</strong>" +
        '<div class="bz-meter" role="img" aria-label="' + Math.round(pct) + '% of the way"><span style="width:' + pct + '%"></span></div>' +
        '<p class="bz-tool__sub">' + Math.round(pct) + "% of the way there</p>" +
        '<div class="bz-stats"><div><small>Time to go</small><b>' + when + "</b></div><div><small>Around</small><b>" + (y === null ? "—" : new Date().getFullYear() + y) + "</b></div></div>" +
        (tip ? '<p class="bz-tool__pill">' + tip + "</p>" : "");
    };
    ["fSpend", "fSaved", "fAdd", "fRate", "fWithdraw"].forEach((id) => document.getElementById(id).addEventListener("input", renderFire));
    renderFire();
  }

  // ─── Currency converter ───────────────────────────────────────────────────
  const fx = document.querySelector('[data-tool="fx"]');
  if (fx) {
    const out = fx.querySelector('[data-out="result"]');
    const from = document.getElementById("xFrom");
    const to = document.getElementById("xTo");
    let fallback = {};
    try { fallback = JSON.parse(root.dataset.fallback || "{}"); } catch (e) { fallback = {}; }
    const cache = {};
    const inflight = {};
    const fmt = (n, code) => {
      try {
        return new Intl.NumberFormat("en-US", { style: "currency", currency: code, maximumFractionDigits: n >= 100 ? 0 : 2 }).format(n);
      } catch (e) {
        return n.toFixed(2) + " " + code;
      }
    };
    const rateOf = (base, target) => {
      if (base === target) return { rate: 1, live: true, date: "" };
      const hit = cache[base];
      if (hit && hit.rates[target]) return { rate: hit.rates[target], live: true, date: hit.date };
      if (fallback[base] && fallback[target]) return { rate: fallback[target] / fallback[base], live: false, date: "" };
      return null;
    };
    const renderFx = () => {
      const amount = num("xAmount");
      const a = from.value;
      const b = to.value;
      const r = rateOf(a, b);
      if (!r) {
        out.textContent = "Rate unavailable for this pair.";
        return;
      }
      out.innerHTML =
        '<span class="bz-tool__label">' + amount.toLocaleString("en-US") + " " + a + " is</span>" +
        '<strong class="bz-tool__big">' + fmt(amount * r.rate, b) + "</strong>" +
        '<p class="bz-tool__sub">1 ' + a + " = " + r.rate.toFixed(r.rate < 1 ? 4 : 2) + " " + b + "</p>" +
        '<ul class="bz-ladder">' + [1, 10, 100, 1000].map((v) => "<li><span>" + v.toLocaleString("en-US") + " " + a + "</span><b>" + fmt(v * r.rate, b) + "</b></li>").join("") + "</ul>" +
        '<p class="bz-tool__src"><i class="' + (r.live ? "is-live" : "") + '"></i>' + (r.live ? "Reference rates" + (r.date ? " · " + r.date : "") : "Offline estimate, live rates unavailable") + "</p>";
    };
    const load = (base) => {
      if (cache[base] || inflight[base] || typeof fetch !== "function") return;
      inflight[base] = true;
      fetch("https://api.frankfurter.dev/v1/latest?base=" + encodeURIComponent(base))
        .then((res) => (res.ok ? res.json() : Promise.reject(new Error("bad status"))))
        .then((data) => {
          cache[base] = { rates: data.rates || {}, date: data.date || "" };
          renderFx();
        })
        .catch(() => {})
        .finally(() => { inflight[base] = false; });
    };
    const update = () => { load(from.value); renderFx(); };
    [from, to].forEach((el) => el.addEventListener("change", update));
    document.getElementById("xAmount").addEventListener("input", renderFx);
    fx.querySelector("[data-swap]").addEventListener("click", () => {
      const t = from.value;
      from.value = to.value;
      to.value = t;
      update();
    });
    const tab = document.getElementById(fx.getAttribute("aria-labelledby"));
    if (tab) tab.addEventListener("click", () => load(from.value));
    renderFx();
  }

  // ─── Investor quiz ────────────────────────────────────────────────────────
  document.querySelectorAll("[data-quiz]").forEach((quiz) => {
    const qs = Array.from(quiz.querySelectorAll("[data-quiz-q]"));
    const result = quiz.querySelector("[data-quiz-result]");
    const step = quiz.querySelector("[data-quiz-step]");
    const bar = quiz.querySelector("[data-quiz-bar]");
    const note = quiz.querySelector(".bz-quiz__note");
    let results = {};
    try { results = JSON.parse(quiz.dataset.results || "{}"); } catch (e) { results = {}; }
    let at = 0;
    let score = 0;
    const show = () => {
      qs.forEach((q, i) => { q.hidden = i !== at; });
      result.hidden = true;
      step.textContent = "Question " + (at + 1) + " of " + qs.length;
      bar.style.width = (at / qs.length) * 100 + "%";
    };
    const finish = () => {
      qs.forEach((q) => { q.hidden = true; });
      const band = score <= qs.length * 1.75 ? "low" : score <= qs.length * 2.4 ? "mid" : "high";
      const r = results[band] || { name: "", text: "" };
      result.querySelector("[data-quiz-name]").textContent = r.name;
      result.querySelector("[data-quiz-text]").textContent = r.text;
      result.hidden = false;
      step.textContent = "Done";
      bar.style.width = "100%";
      if (note) note.hidden = false;
    };
    quiz.querySelectorAll("[data-points]").forEach((btn) => btn.addEventListener("click", () => {
      score += Number(btn.dataset.points);
      at++;
      if (at >= qs.length) finish();
      else show();
    }));
    const reset = quiz.querySelector("[data-quiz-reset]");
    if (reset) reset.addEventListener("click", () => { at = 0; score = 0; show(); });
    if (qs.length) show();
  });
});
